import React, { useEffect, useMemo, type FC } from 'react'
import { useIntl } from 'react-intl'
import { useAtom } from 'jotai'

import {
  Select,
  GridCell,
  SELECT_VARIANT_WITH_CHECKBOX,
  FormElement,
} from 'mailup-web-components'

import { type ConditionProps } from '../../types'
import { filterFormImmerAtom } from '../../state/state'
import {
  UiChannelType,
  type UiActivityMessageSpec,
} from '../../filterConditionTypes'
import ActivityDateDetailsCondition from './ActivityDateDetailsCondition'
import { getAllTagsCallback } from '../../callbacks/specsCallbacks'
import { tagsAtom } from '../../state/specState'
import { useAtomCallback } from 'jotai/utils'
import { type SelectOption } from '../../../../helpers/types'
import useAsyncSelect from '../../../../hooks/useAsyncSelect'
import { MAX_SELECT_ITEMS } from '../../../../config/config'
import {
  fetchEmailMessagesByName,
  fetchEmailMsgById,
  fetchSmsMessagesByName,
  fetchSmsMsgById,
} from '../../services/specServices'
import { activityDefaultMsgOpts } from '../../utils/conditionOptions'
import {
  validateMessageValue,
  validateTagsValue,
} from '../../utils/activitySpecValidation'

const ActivityMessageCondition: FC<ConditionProps> = ({
  blockIndex,
  condIndex,
}) => {
  const intl = useIntl()
  const [filter, setFilter] = useAtom(filterFormImmerAtom)
  const condition = filter.Blocks[blockIndex].Conditions[condIndex]
  const spec = condition.Spec as UiActivityMessageSpec
  const channel = spec.ChannelType

  const isMultiList = filter.IdList === 0
  const messageFeedback = !validateMessageValue(spec, filter)
    ? intl.formatMessage({ id: 'Load.Filters.InputErrorFilterMultilist' })
    : undefined
  const tagsFeedback = !validateTagsValue(spec, filter)
    ? intl.formatMessage({ id: 'Load.Filters.InputErrorFilterMultilist' })
    : undefined

  const defaultMessages: SelectOption[] = useMemo(
    () => activityDefaultMsgOpts(intl),
    [intl],
  )

  const { options, loading, handleSearch, hint, hintNoResults } =
    useAsyncSelect({
      key: `ActivityMessage_${channel}_${blockIndex}_${condIndex}`,
      value: spec.Message,
      maxItems: MAX_SELECT_ITEMS,
      fetchItems:
        channel === UiChannelType.Email
          ? fetchEmailMessagesByName
          : fetchSmsMessagesByName,
      fetchItemsByTerm:
        channel === UiChannelType.Email
          ? fetchEmailMessagesByName
          : fetchSmsMessagesByName,
      fetchItemById:
        channel === UiChannelType.Email ? fetchEmailMsgById : fetchSmsMsgById,
      labels: {
        loading: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusLoading',
        }),
        noResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusNoMatches',
        }),
        moreResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusTooManyItems',
        }),
      },
    })

  const handleExpandCollapse = (c: boolean): void => {
    if (!c) {
      handleSearch('')
    }
  }

  const handleMsgChange = (id: number): void => {
    setFilter((draft) => {
      const draftSpec = draft.Blocks[blockIndex].Conditions[condIndex]
        .Spec as UiActivityMessageSpec
      draftSpec.Message = id
      if (!defaultMessages.some((m) => m.value === id)) {
        draftSpec.Tags = []
      }
    })
  }

  const getAllTags = useAtomCallback(getAllTagsCallback)
  useEffect(() => {
    void getAllTags()
  }, [getAllTags])

  const [tags] = useAtom(tagsAtom)
  const allTagsMapped: SelectOption[] = useMemo(
    () => [
      ...tags.map<SelectOption>((g) => {
        return {
          label: `${g.Id} - ${g.Name}`,
          value: g.Id,
        }
      }),
    ],
    [tags],
  )

  const handleTagsChange = (values: number[]): void => {
    setFilter((draft) => {
      const draftSpec = draft.Blocks[blockIndex].Conditions[condIndex]
        .Spec as UiActivityMessageSpec
      draftSpec.Tags = values
    })
  }

  return (
    <>
      <ActivityDateDetailsCondition
        blockIndex={blockIndex}
        condIndex={condIndex}
      />
      <GridCell span={6}>
        <FormElement
          data-mu-test='filterForm-activity-message_element'
          feedback={messageFeedback}
        >
          {!isMultiList && (
            <Select
              data-mu-test='filterForm-activity-message'
              value={spec.Message}
              options={options}
              loading={loading}
              onChange={handleMsgChange}
              onSearch={handleSearch}
              onExpandCollapse={handleExpandCollapse}
              hint={hint}
              hintNoResults={hintNoResults}
            />
          )}
          {isMultiList && (
            <Select
              data-mu-test='filterForm-activity-message'
              value={spec.Message}
              options={defaultMessages}
              onChange={handleMsgChange}
            />
          )}
        </FormElement>
      </GridCell>
      <GridCell span={6}>
        {spec.Message !== undefined &&
          defaultMessages.some((m) => m.value === spec.Message) && (
            <FormElement
              data-mu-test='filterForm-activity-message_element'
              feedback={tagsFeedback}
            >
              <Select
                placeholder={intl.formatMessage({
                  id: 'Load.Filters.ActivityTagPlaceholder',
                })}
                options={allTagsMapped}
                multiple
                variant={SELECT_VARIANT_WITH_CHECKBOX}
                value={spec.Tags}
                onChange={handleTagsChange}
                data-mu-test='filterForm-activity-tags'
                className='filterForm-activity-tags'
              />
            </FormElement>
          )}
      </GridCell>
    </>
  )
}

export default ActivityMessageCondition
